import { Component, OnInit } from '@angular/core';
import { ActivityService, Activity } from './activity.service';
import { AuthService } from '../../../core/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ActivityDialogComponent } from './activity-dialog/activity-dialog.component'
import Swal from 'sweetalert2';
import { StaffService } from '../../staff/staff.service';


@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit {
  activities: Activity[] = [];
  isLoading = true;
  user: any;
  staffPosition = '';

  constructor(
    private activityService: ActivityService,
    private auth: AuthService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private staffService: StaffService
  ) {}

  ngOnInit(): void {
    this.user = this.auth.getUser();
    this.resolveCurrentStaffPosition();
    this.loadActivities();
  }

  loadActivities(): void {
    this.isLoading = true;
    console.log('[ActivityComponent] 🔄 Loading activities...');
    
    this.activityService.getAll().subscribe({
      next: (res) => {
        console.log('[ActivityComponent] ✅ Data received:', res);
        this.activities = res;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('[ActivityComponent] ❌ Error:', err);
        this.snack.open('Failed to load activities', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }
  
  canAssign(): boolean {
    if (this.isRider()) return false;
    if (this.isLeadershipStaff()) return true;
    return this.user?.role?.toLowerCase() === 'admin' || this.user?.role?.toLowerCase() === 'dispatcher';
  }

  canUpdateStatus(): boolean {
    return this.canManageAction();
  }

  canManageAction(): boolean {
    const role = (this.user?.role || '').toLowerCase();
    if (['admin', 'dispatcher', 'rider'].includes(role)) return true;
    if (this.isLeadershipStaff()) return true;
    return false;
  }

  isLeadershipStaff(): boolean {
    const p = (this.staffPosition || '').toLowerCase().trim();
    return [
      'executive directors',
      'executive director',
      'managers dirrctors',
      'managing director',
      'senior staffs',
      'senior staff'
    ].includes(p);
  }

  isRider(): boolean {
    const role = (this.user?.role || '').toLowerCase().trim();
    const p = (this.staffPosition || '').toLowerCase().trim();
    return role === 'rider' || p === 'riders' || p === 'rider';
  }

  private resolveCurrentStaffPosition(): void {
    this.staffService.getMyProfile().subscribe({
      next: (staff) => {
        this.staffPosition = (staff?.position || '').toString();
      },
      error: () => {
        this.staffPosition = '';
      }
    });
  }

  private getAllowedStatuses(): string[] {
    const all = ['Pending', 'Assigned', 'In Transit', 'Delivered', 'Cancelled'];
    if (this.isRider()) {
      return all.filter(s => s !== 'Pending' && s !== 'Assigned');
    }
    return all;
  }
  openManageDialog(activity: Activity): void {
    console.log('%c[ActivityComponent] 🟦 Opening manage dialog for tracking ID:', 'color: dodgerblue', activity.trackingId);
  
    const dialogRef = this.dialog.open(ActivityDialogComponent, {
      width: '450px',
      maxWidth: '95vw',
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: {
        activity,
        canAssign: this.canAssign(),
        statuses: this.getAllowedStatuses()
      }
    });
  
    dialogRef.afterClosed().subscribe((result) => {
      console.log('%c[ActivityComponent] 📦 Dialog closed with result:', 'color: #888', result);
  
      if (result) {
        const { riderId, riderName, status } = result;
        console.log('%c[ActivityComponent] 🔍 Parsed result:', 'color: teal', { riderId, riderName, status });
  
        // 1️⃣ Assign Rider
        if (riderId && riderId !== activity.assignedRider) {
          console.log('%c[ActivityComponent] 🚴 Assigning rider...', 'color: orange', { trackingId: activity.trackingId, riderId, riderName });
  
          this.activityService.assignRider(
            activity.trackingId,
            riderId.toString(), // 👈 Convert to string
            riderName
          ).subscribe({
            next: () => {
              console.log('%c[ActivityComponent] ✅ Rider assigned successfully', 'color: green');
              this.snack.open(`Rider ${riderName} assigned successfully`, 'OK', { duration: 3000 });
              this.loadActivities();
            },
            error: (err) => {
              console.error('%c[ActivityComponent] ❌ Rider assignment failed:', 'color: red', err);
              this.snack.open('Failed to assign rider', 'Close', { duration: 3000 });
            }
          });
          
        } else {
          console.log('%c[ActivityComponent] ⚪ No rider change detected, skipping rider assignment.', 'color: gray');
        }
  
        // 2️⃣ Update Status
        if (status && status !== activity.status) {
          console.log('%c[ActivityComponent] 🔁 Updating delivery status...', 'color: orange', { trackingId: activity.trackingId, newStatus: status });
  
          this.activityService.updateStatus(activity.trackingId, status).subscribe({
            next: (res) => {
              console.log('%c[ActivityComponent] ✅ Status update successful:', 'color: green', res);
              this.snack.open(`Status updated to ${status}`, 'OK', { duration: 3000 });
              this.loadActivities();
            },
            error: (err) => {
              console.error('%c[ActivityComponent] ❌ Status update failed:', 'color: red', err);
              this.snack.open('Failed to update status', 'Close', { duration: 3000 });
            }
          });
        } else {
          console.log('%c[ActivityComponent] ⚪ No status change detected, skipping update.', 'color: gray');
        }
      } else {
        console.log('%c[ActivityComponent] ⚫ Dialog closed without saving changes.', 'color: gray');
      }
    });
  }
  
  

  async markAsPaid(activity: Activity): Promise<void> {
    const result = await Swal.fire({
      title: 'Mark as Paid?',
      text: `Mark delivery ${activity.trackingId} as paid?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, mark as paid',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    this.activityService.markAsPaid(activity.trackingId).subscribe({
      next: () => {
        Swal.fire('Success', 'Marked as paid successfully.', 'success');
        this.loadActivities();
      },
      error: (err) => {
        console.error('[ActivityComponent] ❌ Failed to mark as paid:', err);
        Swal.fire('Error', 'Failed to mark as paid.', 'error');
      }
    });
  }
}
